import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AlertTriangle, ClipboardCheck, RefreshCw, ShieldCheck } from 'lucide-react';
import { apiBase, authHeaders } from './utils/authHeaders';
import { apiErrorMessage } from './utils/apiError';

const SECTIONS = [
  ['external_inspections', 'External inspections'],
  ['configured_checklists', 'Configured checklists'],
  ['checklist_responses', 'Checklist responses'],
  ['temperature_logs', 'Temperature logs'],
  ['safety_incidents', 'Safety incidents'],
  ['training_records', 'Training records'],
  ['inspection_actions', 'Inspection actions'],
];

function localSiteId() {
  try { return localStorage.getItem('current_location_id') || ''; } catch { return ''; }
}

function recordTitle(record) {
  return record.name || record.description || record.inspection_type || record.equipment_name ||
    record.equipment_id || record.training_type || record.type || record.incident_type ||
    record.item_id || 'Evidence record';
}

function recordDate(record) {
  return record.timestamp || record.reported_at || record.inspection_date || record.created_at ||
    record.training_date || record.updated_at || null;
}

function recordDetail(record) {
  const details = [];
  if (record.inspector_name) details.push(`Inspector: ${record.inspector_name}`);
  if (record.inspecting_authority) details.push(`Authority: ${record.inspecting_authority}`);
  if (record.violations?.length) details.push(`Findings: ${record.violations.join('; ')}`);
  if (record.corrective_actions?.length) details.push(`Corrective actions: ${record.corrective_actions.join('; ')}`);
  if (record.source_reference) details.push(`Source reference: ${record.source_reference}`);
  if (record.source_document_id) details.push(`Linked vault document: ${record.source_document_id}`);
  if (record.source_reviewed_at) details.push(`Source checked by ${record.source_reviewed_by_name || 'manager'} at ${new Date(record.source_reviewed_at).toLocaleString()} (not a compliance assessment)`);
  if (record.status) details.push(`Status: ${record.status.replace(/_/g, ' ').toLowerCase()}`);
  if (record.created_by_name) details.push(`Task opened by: ${record.created_by_name}`);
  if (record.assigned_to_name) details.push(`Assigned to: ${record.assigned_to_name}`);
  if (record.due_date) details.push(`Due: ${new Date(record.due_date).toLocaleDateString()}`);
  if (record.resolution) details.push(`Reported resolution: ${record.resolution}`);
  if (record.evidence_reference) details.push(`Resolution evidence reference: ${record.evidence_reference}`);
  if (record.evidence_document_id) details.push(`Linked resolution file: ${record.evidence_document_id}`);
  if (record.resolved_by_name) details.push(`Resolution submitted by: ${record.resolved_by_name}`);
  if (record.reviewed_at) details.push(`Closure reference checked by ${record.reviewed_by_name || 'manager'} at ${new Date(record.reviewed_at).toLocaleString()}`);
  if (record.review_notes) details.push(`Review notes: ${record.review_notes}`);
  if (record.closure_meaning) details.push('Workflow closure only; this does not establish compliance or authorize safety release.');
  if (details.length) return details.join('\n');
  if (record.question) return record.question;
  if (record.description) return record.description;
  if (record.response !== undefined) return `Response: ${record.response}`;
  if (record.temperature !== undefined) return `Reading: ${record.temperature}${record.unit ? ` ${record.unit}` : ''}`;
  if (record.status) return `Recorded status: ${record.status}`;
  return '';
}

const SafetyHealth = () => {
  const route = useLocation();
  const [siteId, setSiteId] = useState(localSiteId);
  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [reviewingId, setReviewingId] = useState('');
  const [actionBusyId, setActionBusyId] = useState('');
  const [assignees, setAssignees] = useState([]);
  const [sourceDocuments, setSourceDocuments] = useState([]);
  const [sourceDocumentsError, setSourceDocumentsError] = useState('');
  const [assigneesLoading, setAssigneesLoading] = useState(false);
  const [assigneeError, setAssigneeError] = useState('');
  const submissionKeyRef = useRef(null);
  const actionKeysRef = useRef(new Map());
  const resolutionKeysRef = useRef(new Map());
  const reviewKeysRef = useRef(new Map());
  const assigneesRequestRef = useRef(false);
  const assigneeSequenceRef = useRef(0);
  const [activeSection, setActiveSection] = useState('external_inspections');
  const loadSequenceRef = useRef(0);

  useEffect(() => {
    const updateSite = (event) => {
      if (event?.type === 'organizationChanged' || event?.key === 'current_organization_id') {
        setSiteId('');
        return;
      }
      const nextSite = localSiteId();
      setSiteId((current) => current === nextSite ? current : nextSite);
    };
    window.addEventListener('locationChanged', updateSite);
    window.addEventListener('organizationChanged', updateSite);
    window.addEventListener('storage', updateSite);
    return () => {
      window.removeEventListener('locationChanged', updateSite);
      window.removeEventListener('organizationChanged', updateSite);
      window.removeEventListener('storage', updateSite);
    };
  }, []);

  useEffect(() => {
    setPack(null);
    setAssignees([]);
    setAssigneeError('');
    assigneeSequenceRef.current += 1;
    assigneesRequestRef.current = false;
    loadSequenceRef.current += 1;
  }, [siteId]);

  const loadPack = useCallback(async () => {
    const sequence = ++loadSequenceRef.current;
    if (!siteId) {
      if (sequence === loadSequenceRef.current) setPack(null);
      setError('Select an authorized site to view inspection evidence.');
      if (sequence === loadSequenceRef.current) setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${apiBase()}/api/mobile/safety/inspection-readiness`, {
        headers: authHeaders({ locationId: siteId }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(apiErrorMessage(body.detail, `Could not load inspection evidence (${response.status})`));
      }
      const data = await response.json();
      if (sequence === loadSequenceRef.current) setPack(data);
    } catch (err) {
      if (sequence === loadSequenceRef.current) setError(err.message || 'Could not load inspection evidence.');
    } finally {
      if (sequence === loadSequenceRef.current) setLoading(false);
    }
  }, [siteId]);

  useEffect(() => { loadPack(); }, [loadPack]);

  useEffect(() => {
    let cancelled = false;
    setSourceDocuments([]);
    setSourceDocumentsError('');
    const actions = pack?.sections?.inspection_actions?.records || [];
    const canLinkEvidence = pack?.can_record_external_inspection || actions.some((action) => action.can_submit_resolution);
    if (!siteId || !canLinkEvidence) return undefined;
    fetch(`${apiBase()}/api/documents`, { headers: authHeaders({ locationId: siteId }) })
      .then(async (response) => {
        if (!response.ok) throw new Error(`Could not load site documents (${response.status})`);
        return response.json();
      })
      .then((documents) => {
        if (cancelled || localSiteId() !== siteId) return;
        const rows = Array.isArray(documents) ? documents : (documents.documents || []);
        setSourceDocuments(rows.filter((document) =>
          document.category === 'Compliance' && document.scope?.location_id === siteId && !document.deleted
        ));
      })
      .catch((err) => {
        if (!cancelled) setSourceDocumentsError(err.message || 'Could not load site documents.');
      });
    return () => { cancelled = true; };
  }, [siteId, pack?.can_record_external_inspection, pack?.sections?.inspection_actions?.records]);

  const section = pack?.sections?.[activeSection];
  const activeLabel = SECTIONS.find(([id]) => id === activeSection)?.[1] || 'Evidence';

  const submitInspection = async (event) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setSaving(true);
    setSaveError('');
    try {
      submissionKeyRef.current = submissionKeyRef.current || (
        window.crypto?.randomUUID?.() || `inspection-${Date.now()}-${Math.random().toString(36).slice(2)}`
      );
      const response = await fetch(`${apiBase()}/api/mobile/safety/external-inspections`, {
        method: 'POST',
        headers: { ...authHeaders({ locationId: siteId }), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          idempotency_key: submissionKeyRef.current,
          inspection_type: form.get('inspection_type'),
          inspector_name: form.get('inspector_name'),
          inspecting_authority: form.get('inspecting_authority') || null,
          inspection_date: new Date(`${form.get('inspection_date')}T12:00:00`).toISOString(),
          reported_outcome: form.get('reported_outcome'),
          findings: form.get('findings') ? [form.get('findings')] : [],
          corrective_actions: form.get('corrective_actions') ? [form.get('corrective_actions')] : [],
          source_reference: form.get('source_reference') || null,
          source_document_id: form.get('source_document_id') || null,
          notes: form.get('notes') || null,
        }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(apiErrorMessage(body.detail, `Could not record inspection (${response.status})`));
      }
      event.currentTarget.reset();
      submissionKeyRef.current = null;
      await loadPack();
    } catch (err) {
      setSaveError(err.message || 'Could not record inspection.');
    } finally {
      setSaving(false);
    }
  };

  const reviewSource = async (inspectionId) => {
    const confirmed = window.confirm('Confirm that you checked this transcription against its referenced original source. This does not verify corrective work or assess compliance.');
    if (!confirmed) return;
    setReviewingId(inspectionId);
    setSaveError('');
    try {
      const response = await fetch(`${apiBase()}/api/mobile/safety/external-inspections/${encodeURIComponent(inspectionId)}/source-review`, {
        method: 'POST',
        headers: { ...authHeaders({ locationId: siteId }), 'Content-Type': 'application/json' },
        body: JSON.stringify({ source_reference_confirmed: true }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(apiErrorMessage(body.detail, `Could not record source review (${response.status})`));
      }
      await loadPack();
    } catch (err) {
      setSaveError(err.message || 'Could not record source review.');
    } finally {
      setReviewingId('');
    }
  };

  const loadAssignees = async () => {
    if (assignees.length || assigneesRequestRef.current) return;
    const sequence = ++assigneeSequenceRef.current;
    const requestedSite = siteId;
    assigneesRequestRef.current = true;
    setAssigneesLoading(true);
    setAssigneeError('');
    try {
      const response = await fetch(`${apiBase()}/api/mobile/safety/inspection-action-assignees`, {
        headers: authHeaders({ locationId: siteId }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(apiErrorMessage(body.detail, `Could not load site staff (${response.status})`));
      }
      const body = await response.json();
      if (sequence === assigneeSequenceRef.current && requestedSite === localSiteId()) {
        setAssignees(Array.isArray(body.assignees) ? body.assignees : []);
      }
    } catch (err) {
      if (sequence === assigneeSequenceRef.current) setAssigneeError(err.message || 'Could not load site staff.');
    } finally {
      if (sequence === assigneeSequenceRef.current) {
        assigneesRequestRef.current = false;
        setAssigneesLoading(false);
      }
    }
  };

  const createAction = async (event, inspectionId) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setActionBusyId(inspectionId);
    setSaveError('');
    try {
      if (!actionKeysRef.current.has(inspectionId)) {
        actionKeysRef.current.set(inspectionId, window.crypto?.randomUUID?.() || `action-${Date.now()}-${Math.random().toString(36).slice(2)}`);
      }
      const response = await fetch(`${apiBase()}/api/mobile/safety/external-inspections/${encodeURIComponent(inspectionId)}/actions`, {
        method: 'POST',
        headers: { ...authHeaders({ locationId: siteId }), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          idempotency_key: actionKeysRef.current.get(inspectionId),
          description: form.get('description'),
          assigned_to_user_id: form.get('assigned_to_user_id'),
          due_date: form.get('due_date') ? new Date(`${form.get('due_date')}T12:00:00`).toISOString() : null,
        }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(apiErrorMessage(body.detail, `Could not create corrective action (${response.status})`));
      }
      event.currentTarget.reset();
      actionKeysRef.current.delete(inspectionId);
      await loadPack();
    } catch (err) {
      setSaveError(err.message || 'Could not create corrective action.');
    } finally {
      setActionBusyId('');
    }
  };

  const submitActionResolution = async (event, action) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setActionBusyId(action.id);
    setSaveError('');
    try {
      if (!resolutionKeysRef.current.has(action.id)) {
        resolutionKeysRef.current.set(action.id, window.crypto?.randomUUID?.() || `resolution-${Date.now()}-${Math.random().toString(36).slice(2)}`);
      }
      const response = await fetch(`${apiBase()}/api/mobile/safety/inspection-actions/${encodeURIComponent(action.id)}/resolution`, {
        method: 'POST',
        headers: { ...authHeaders({ locationId: siteId }), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          idempotency_key: resolutionKeysRef.current.get(action.id),
          resolution: form.get('resolution'),
          evidence_reference: form.get('evidence_reference'),
          evidence_document_id: form.get('evidence_document_id') || null,
        }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(apiErrorMessage(body.detail, `Could not submit resolution (${response.status})`));
      }
      event.currentTarget.reset();
      resolutionKeysRef.current.delete(action.id);
      await loadPack();
    } catch (err) {
      setSaveError(err.message || 'Could not submit resolution.');
    } finally {
      setActionBusyId('');
    }
  };

  const reviewAction = async (action) => {
    if (!window.confirm('Confirm that you checked the submitted resolution reference. Closing this workflow does not establish compliance or authorize safety release.')) return;
    setActionBusyId(action.id);
    setSaveError('');
    try {
      if (!reviewKeysRef.current.has(action.id)) {
        reviewKeysRef.current.set(action.id, window.crypto?.randomUUID?.() || `review-${Date.now()}-${Math.random().toString(36).slice(2)}`);
      }
      const response = await fetch(`${apiBase()}/api/mobile/safety/inspection-actions/${encodeURIComponent(action.id)}/review`, {
        method: 'POST',
        headers: { ...authHeaders({ locationId: siteId }), 'Content-Type': 'application/json' },
        body: JSON.stringify({ idempotency_key: reviewKeysRef.current.get(action.id), evidence_reference_checked: true }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(apiErrorMessage(body.detail, `Could not close corrective action (${response.status})`));
      }
      reviewKeysRef.current.delete(action.id);
      await loadPack();
    } catch (err) {
      setSaveError(err.message || 'Could not close corrective action.');
    } finally {
      setActionBusyId('');
    }
  };

  return (
    <main className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 pb-4">
        <div className="flex items-center gap-3">
          <ShieldCheck aria-hidden="true" className="h-6 w-6 text-emerald-700" />
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Inspection evidence</h1>
            <p className="text-sm text-gray-600">Evidence recorded for the selected site</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
        {route.pathname.startsWith('/mobile/') && (
          <Link to="/mobile/safety" className="inline-flex h-10 items-center border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50">
            Safety
          </Link>
        )}
        <button
          type="button"
          onClick={loadPack}
          disabled={loading}
          aria-label="Refresh inspection evidence"
          title="Refresh inspection evidence"
          className="inline-flex h-10 w-10 items-center justify-center rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw aria-hidden="true" className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
        </div>
      </header>

      {error && (
        <div role="alert" className="mt-5 flex items-start gap-2 border-l-4 border-amber-600 bg-amber-50 p-3 text-sm text-amber-950">
          <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {loading && <p role="status" className="py-8 text-sm text-gray-600">Loading evidence…</p>}

      {!loading && pack && (
        <>
          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1 text-sm text-gray-600">
            <span>Site: <span className="font-medium text-gray-900">{pack.location_id}</span></span>
            <span>Prepared: {new Date(pack.prepared_at).toLocaleString()}</span>
            <span className="font-medium text-amber-800">Assessment: not performed</span>
          </div>
          <p className="mt-3 border-l-4 border-amber-500 bg-amber-50 p-3 text-sm text-amber-950">
            {pack.notice}
          </p>

          {pack.can_record_external_inspection && (
            <details className="mt-5 border border-gray-300 bg-white p-4">
              <summary className="cursor-pointer text-sm font-semibold text-gray-900">Record an external inspection</summary>
              <p className="mt-2 text-sm text-gray-600">Transcribe the report as received. This entry does not verify findings or assess compliance.</p>
              {saveError && <p role="alert" className="mt-2 text-sm text-red-700">{saveError}</p>}
              <form className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2" onSubmit={submitInspection}>
                <label className="text-sm text-gray-700">Inspection type
                  <select required name="inspection_type" className="mt-1 block h-10 w-full border border-gray-300 px-2">
                    <option value="food_safety">Food safety</option><option value="environmental_health">Environmental health</option><option value="fire_safety">Fire safety</option><option value="other">Other</option>
                  </select>
                </label>
                <label className="text-sm text-gray-700">Inspection date
                  <input required name="inspection_date" type="date" className="mt-1 block h-10 w-full border border-gray-300 px-2" />
                </label>
                <label className="text-sm text-gray-700">Inspector name
                  <input required maxLength="160" name="inspector_name" className="mt-1 block h-10 w-full border border-gray-300 px-2" />
                </label>
                <label className="text-sm text-gray-700">Authority
                  <input maxLength="160" name="inspecting_authority" className="mt-1 block h-10 w-full border border-gray-300 px-2" />
                </label>
                <label className="text-sm text-gray-700">Reported outcome
                  <select required name="reported_outcome" className="mt-1 block h-10 w-full border border-gray-300 px-2">
                    <option value="not_recorded">Not recorded</option><option value="satisfactory">Reported satisfactory</option><option value="actions_required">Actions required</option><option value="follow_up_required">Follow-up required</option>
                  </select>
                </label>
                <label className="text-sm text-gray-700">Source document reference
                  <input maxLength="240" name="source_reference" className="mt-1 block h-10 w-full border border-gray-300 px-2" />
                </label>
                <label className="text-sm text-gray-700">Link a site Compliance file from the secure vault
                  <select name="source_document_id" defaultValue="" className="mt-1 block h-10 w-full border border-gray-300 px-2">
                    <option value="">No file linked</option>
                    {sourceDocuments.map((document) => <option key={document.id} value={document.id}>{document.filename || document.id}</option>)}
                  </select>
                </label>
                {sourceDocumentsError && <p role="status" className="text-xs text-amber-800">{sourceDocumentsError}. You can still record the source reference without linking a vault file.</p>}
                <label className="text-sm text-gray-700 sm:col-span-2">Finding(s)
                  <textarea maxLength="4000" name="findings" rows="2" className="mt-1 block w-full border border-gray-300 p-2" />
                </label>
                <label className="text-sm text-gray-700 sm:col-span-2">Corrective action(s)
                  <textarea maxLength="4000" name="corrective_actions" rows="2" className="mt-1 block w-full border border-gray-300 p-2" />
                </label>
                <label className="text-sm text-gray-700 sm:col-span-2">Notes
                  <textarea maxLength="4000" name="notes" rows="2" className="mt-1 block w-full border border-gray-300 p-2" />
                </label>
                <button disabled={saving} className="h-10 w-fit border border-emerald-800 bg-emerald-800 px-4 text-sm font-medium text-white disabled:opacity-50" type="submit">{saving ? 'Recording…' : 'Record inspection'}</button>
              </form>
            </details>
          )}

          {saveError && <p role="alert" className="mt-3 text-sm text-red-700">{saveError}</p>}

            <section aria-label="Evidence sources" className="mt-6 grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-4">
            {SECTIONS.map(([id, label]) => {
              const item = pack.sections[id];
              const selected = id === activeSection;
              return (
                <button
                  key={id}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => setActiveSection(id)}
                  className={`min-h-20 border p-3 text-left ${selected ? 'border-emerald-700 bg-emerald-50' : 'border-gray-300 bg-white hover:bg-gray-50'}`}
                >
                  <span className="block text-sm font-medium text-gray-900">{label}</span>
                  <span className="mt-1 block text-xs text-gray-600">
                    {!item || item.state === 'NOT_CONFIGURED' ? 'Not configured' : `${item.record_count} records`}
                  </span>
                </button>
              );
            })}
          </section>

          <section className="mt-7" aria-labelledby="inspection-evidence-list">
            <div className="flex items-center justify-between border-b border-gray-200 pb-2">
              <h2 id="inspection-evidence-list" className="text-base font-semibold text-gray-900">{activeLabel}</h2>
              {section?.truncated && <span className="text-xs text-gray-600">Showing latest {section.sample_limit} of {section.record_count}</span>}
            </div>
            {section?.state === 'NOT_CONFIGURED' ? (
              <p className="py-5 text-sm text-gray-600">No source collection is configured.</p>
            ) : section?.state === 'EMPTY' ? (
              <p className="py-5 text-sm text-gray-600">The source is configured, but no records were found for this site.</p>
            ) : (
              <ul className="divide-y divide-gray-200">
                {(section?.records || []).map((record) => (
                  <li key={record.id || `${recordTitle(record)}-${recordDate(record)}`} className="flex gap-3 py-4">
                    <ClipboardCheck aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                        <span className="break-words text-sm font-medium text-gray-900">{recordTitle(record)}</span>
                        {recordDate(record) && <time className="text-xs text-gray-600">{new Date(recordDate(record)).toLocaleString()}</time>}
                      </div>
                      {recordDetail(record) && <p className="mt-1 whitespace-pre-wrap break-words text-sm text-gray-700">{recordDetail(record)}</p>}
                      {activeSection === 'external_inspections' && pack.can_record_external_inspection && record.can_review_source && (
                        <button
                          type="button"
                          disabled={reviewingId === record.id}
                          onClick={() => reviewSource(record.id)}
                          className="mt-2 inline-flex h-9 items-center border border-gray-400 px-3 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                        >
                          {reviewingId === record.id ? 'Recording review…' : 'Verify source transcription'}
                        </button>
                      )}
                      {activeSection === 'external_inspections' && pack.can_record_external_inspection && record.record_kind === 'external_inspection_transcription' && (
                        <details className="mt-2" onToggle={(event) => { if (event.currentTarget.open) loadAssignees(); }}>
                          <summary className="cursor-pointer text-sm font-medium text-gray-800">Track a corrective action</summary>
                          {assigneeError && <p role="alert" className="mt-2 text-sm text-red-700">{assigneeError}</p>}
                          {assigneesLoading && <p role="status" className="mt-2 text-sm text-gray-600">Loading site staff…</p>}
                          <form className="mt-2 grid max-w-xl grid-cols-1 gap-2 sm:grid-cols-2" onSubmit={(event) => createAction(event, record.id)}>
                            <label className="text-sm text-gray-700 sm:col-span-2">Action
                              <input required minLength="3" maxLength="2000" name="description" className="mt-1 block h-10 w-full border border-gray-300 px-2" />
                            </label>
                            <label className="text-sm text-gray-700 sm:col-span-2">Assign to
                              <select required name="assigned_to_user_id" defaultValue="" disabled={assigneesLoading || assignees.length === 0} className="mt-1 block h-10 w-full border border-gray-300 px-2">
                                <option value="" disabled>{assignees.length ? 'Select a site team member' : 'No eligible site team members'}</option>
                                {assignees.map((person) => <option key={person.id} value={person.id}>{person.name}</option>)}
                              </select>
                            </label>
                            <label className="text-sm text-gray-700">Due date
                              <input name="due_date" type="date" className="mt-1 block h-10 w-full border border-gray-300 px-2" />
                            </label>
                            <button disabled={actionBusyId === record.id || assigneesLoading || assignees.length === 0} className="mt-5 h-10 w-fit border border-gray-500 px-3 text-sm font-medium text-gray-800 disabled:opacity-50" type="submit">
                              {actionBusyId === record.id ? 'Saving…' : 'Create action'}
                            </button>
                          </form>
                        </details>
                      )}
                      {activeSection === 'inspection_actions' && record.can_submit_resolution && (
                        <form className="mt-3 grid max-w-xl grid-cols-1 gap-2 sm:grid-cols-2" onSubmit={(event) => submitActionResolution(event, record)}>
                          <label className="text-sm text-gray-700 sm:col-span-2">What was done?
                            <textarea required minLength="3" maxLength="2000" name="resolution" rows="2" className="mt-1 block w-full border border-gray-300 p-2" />
                          </label>
                          <label className="text-sm text-gray-700">Evidence reference
                            <input required maxLength="240" name="evidence_reference" className="mt-1 block h-10 w-full border border-gray-300 px-2" />
                          </label>
                          <label className="text-sm text-gray-700 sm:col-span-2">Link a site Compliance file from the secure vault (optional)
                            <select name="evidence_document_id" defaultValue="" className="mt-1 block h-10 w-full border border-gray-300 px-2">
                              <option value="">No digitized file; reference recorded separately</option>
                              {sourceDocuments.map((document) => <option key={document.id} value={document.id}>{document.title || document.filename || document.id}</option>)}
                            </select>
                          </label>
                          {sourceDocumentsError && <p role="status" className="text-xs text-amber-800 sm:col-span-2">{sourceDocumentsError}. A paper or physical reference can still be recorded without a vault link.</p>}
                          <button disabled={actionBusyId === record.id} className="mt-5 h-10 w-fit border border-gray-500 px-3 text-sm font-medium text-gray-800 disabled:opacity-50" type="submit">
                            {actionBusyId === record.id ? 'Saving…' : 'Submit for review'}
                          </button>
                        </form>
                      )}
                      {activeSection === 'inspection_actions' && record.can_review_closure && (
                        <button
                          type="button"
                          disabled={actionBusyId === record.id}
                          onClick={() => reviewAction(record)}
                          className="mt-2 inline-flex h-9 items-center border border-gray-400 px-3 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                        >
                          {actionBusyId === record.id ? 'Reviewing…' : 'Review evidence and close workflow'}
                        </button>
                      )}
                      {record.id && <p className="mt-1 break-all font-mono text-xs text-gray-500">Reference: {record.id}</p>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </main>
  );
};

export default SafetyHealth;
